/**
 * Owner sidebar layout — wraps every /owner/* page.
 *
 * Server derives brand_id from JWT for every owner API call, so this
 * layout has no brand selector.
 *
 * PR-Audit-02A G-01 (2026-06-19) — responsive shell:
 *   * At `<md` (768px): sidebar collapses into a fixed off-canvas
 *     drawer with backdrop. Hamburger button in header opens it.
 *     Tapping the backdrop OR selecting any nav item closes it.
 *   * At `≥md`: layout UNCHANGED (sidebar sits inline in the flex
 *     row exactly as it always did). Desktop non-regression preserved.
 *   * Header + main padding shrink to `px-4` on mobile, restore to
 *     `px-8` on `≥md`.
 *   * Touch targets ≥44×44 px (hamburger button, nav items, logout).
 *   * No new design system, no forked components, single source of
 *     truth (`lib/rbac.js#NAV_ENTRIES`) preserved.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Menu, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { qk } from '@/lib/queryKeys';
import NotificationBellButton from '@/components/owner/NotificationBellButton';
import NotificationSlideOver from '@/components/owner/NotificationSlideOver';
import {
  NAV_ENTRIES, filterNavEntries, filterNavEntriesByCapabilities,
  CONSOLE_TITLES, ROLE_LABELS, ROLES,
} from '@/lib/rbac';
import { useEffectiveCapabilities } from '@/lib/capabilities';

/**
 * PR-3 Q4 — `[NEW]` pill auto-expiry constant.
 *
 * The Manual Booking sidebar entry surfaces a small `NEW` badge to
 * signal the new operational surface to merchants. The badge fades
 * out automatically 30 days after the PR-3 launch date so the
 * sidebar settles into its long-term shape without an operator-side
 * cleanup task.
 */
const PILL_LAUNCH_DATE = '2026-06-17';
const PILL_VISIBLE_UNTIL = new Date(
  new Date(PILL_LAUNCH_DATE).getTime() + 30 * 24 * 60 * 60 * 1000,
);
const isNewPillVisible = () => Date.now() < PILL_VISIBLE_UNTIL.getTime();

/* R-2 — sidebar manifest now lives in `lib/rbac.js#NAV_ENTRIES` with
 * per-entry `requiredRoles`. OwnerLayout consumes that single source
 * of truth via `filterNavEntries(NAV_ENTRIES, role)` so cashier + GM
 * see only the entries they're permitted to act on. No `role ===`
 * conditional lives inside this file.
 */

export default function OwnerLayout() {
  const { token, user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const brandId = user?.brand_id;
  const { data: brand } = useQuery({
    queryKey: qk.ownerBrand(brandId || 'self'),
    queryFn: () => api.ownerBrand(token),
    enabled: !!token && !!brandId,
    staleTime: 60_000,
  });

  const onLogout = () => {
    signOut();
    navigate('/login', { replace: true });
  };

  // PR-4b — Notification slide-over open/closed state.
  const [notifOpen, setNotifOpen] = useState(false);
  // PR-Audit-02A G-01 — mobile drawer open/closed state.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);

  // Sub-phase RBAC-C1 (2026-06-18) — capability-driven nav filter.
  // Fetches `/api/owner/me/capabilities` once per session and filters
  // NAV_ENTRIES by the actor's resolved capability set. While the
  // cap-set is still in-flight (or empty due to a fresh seed), we
  // gracefully degrade to the R-2 role-based filter so the sidebar
  // never flashes empty for a logged-in user.
  //
  // Tier-1 invariant preserved — capability checks first, role-based
  // fallback only when capabilities can't be resolved yet. No
  // `user.role === '…'` JSX in this component.
  //
  // **Phase MONETIZATION-ITEMS-1-3 (2026-06-19)** — Pro-bundle nav
  // entries (per ONESTAMP_PLAN_MATRIX §2.3) are hidden when the
  // brand's `pro_enabled` is false. `brandFlags = null` (brand fetch
  // still in-flight) bypasses the plan filter to avoid
  // flash-of-empty-nav — same defensive pattern as RBAC-C1.
  // Super Admin has no brand_id → brandFlags stays null → all entries
  // visible (intentional: SA spans all brands).
  const { capSet, resolution, isLoading: capsLoading } = useEffectiveCapabilities();
  const capabilitiesReady = !capsLoading && capSet.size > 0;
  const brandFlags = brand
    ? { proEnabled: brand.pro_enabled === true }
    : null;
  const visibleNav = capabilitiesReady
    ? filterNavEntriesByCapabilities(NAV_ENTRIES, capSet, brandFlags)
    : filterNavEntries(NAV_ENTRIES, user?.role, brandFlags);

  const consoleTitle = CONSOLE_TITLES[user?.role] || 'Merchant Pro';
  // Effective role name (e.g. "Supervisor" for users with custom-role
  // assignment) preferred when capabilities resolve via `assigned_role`;
  // otherwise the base enum label.
  const roleLabel = resolution?.effective_role_name
    || ROLE_LABELS[user?.role]
    || '—';

  // Phase R4 (2026-06-29) — pending receipt-claim count for the sidebar
  // badge. Polls every 30 s. Visible to brand_owner / super_admin
  // (the only roles with Receipt Claims nav entry). Count is derived
  // from the `/api/owner/receipt-claims` endpoint's `counts` aggregate
  // so we avoid introducing a new backend route.
  const canReviewClaims =
    user?.role === ROLES.BRAND_OWNER || user?.role === ROLES.SUPER_ADMIN;
  const receiptCountsQ = useQuery({
    queryKey: qk.ownerReceiptClaimsPendingCount(),
    queryFn: () =>
      api.ownerListReceiptClaims(token, { status: 'pending_owner_review', limit: 1 }),
    enabled: !!token && canReviewClaims,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const pendingClaimsCount =
    receiptCountsQ.data?.counts?.pending_owner_review ?? 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Mobile drawer backdrop — visible only when drawer is open at <md */}
      {mobileNavOpen ? (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={closeMobileNav}
          data-testid="owner-mobile-nav-backdrop"
          aria-hidden="true"
        />
      ) : null}

      {/* Sidebar
        * Desktop (md+): inline flex column, w-64, sits to left of main.
        * Mobile (<md): fixed off-canvas drawer; translates in/out based
        *   on `mobileNavOpen`. `transition-transform` keeps the slide
        *   smooth without third-party animation libs (reuse-first). */}
      <aside
        className={
          'w-64 shrink-0 border-r border-white/5 bg-[#0E0E13] flex flex-col ' +
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ' +
          (mobileNavOpen ? 'translate-x-0 ' : '-translate-x-full ') +
          'md:relative md:translate-x-0 md:transition-none'
        }
        data-testid={`sidebar-role-${user?.role || 'unknown'}`}
      >
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/5">
          <img
            src="/onestamp-logo.png"
            alt="OneStamp"
            className="w-10 h-10 rounded-2xl shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gold/85 uppercase tracking-wider font-semibold">
              OneStamp
            </div>
            <div className="text-sm font-semibold truncate" title={brand?.name}>
              {brand?.name ?? '—'}
            </div>
          </div>
          {/* Mobile-only close (X) button — preserves drawer-dismiss UX
            * even when the user lands directly on a nav item OR taps a
            * stray location inside the drawer body. */}
          <button
            type="button"
            onClick={closeMobileNav}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-2xl text-white/70 hover:bg-white/5 hover:text-white"
            data-testid="owner-mobile-nav-close"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav
          className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"
          data-testid="sidebar-nav-list"
        >
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const isReceiptClaims = n.to === '/owner/receipt-claims';
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={closeMobileNav}
                data-testid={`sidebar-nav-${n.to.replace(/\//g, '-').replace(/^-/, '')}`}
                className={({ isActive }) =>
                  'flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors min-h-[44px] ' +
                  (isActive
                    ? 'bg-gold/10 text-gold border border-gold/25 shadow-glow-gold-sm'
                    : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent')
                }
              >
                <Icon size={16} />
                <span className="flex-1">{n.label}</span>
                {isReceiptClaims && pendingClaimsCount > 0 ? (
                  <span
                    data-testid="sidebar-receipt-claims-badge"
                    className="text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-black grid place-items-center shadow-glow-gold-sm"
                    aria-label={`${pendingClaimsCount} pending receipt claims`}
                  >
                    {pendingClaimsCount > 99 ? '99+' : pendingClaimsCount}
                  </span>
                ) : null}
                {n.newPill && isNewPillVisible() ? (
                  <span
                    data-testid="sidebar-new-pill"
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gold/20 text-gold border border-gold/30 uppercase tracking-wider"
                  >
                    NEW
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-white/5">
          <button
            onClick={onLogout}
            data-testid="owner-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-2xl text-sm text-white/70 hover:bg-white/5 hover:text-white min-h-[44px]"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-4 md:px-8 border-b border-white/5 flex items-center justify-between gap-2">
          {/* Mobile-only hamburger toggle. 44×44 touch target (Apple HIG). */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 -ml-2 rounded-2xl text-white/80 hover:bg-white/5 hover:text-white"
            data-testid="owner-mobile-nav-toggle"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
          >
            <Menu size={20} />
          </button>
          <div
            className="hidden sm:block text-sm text-white/60 truncate min-w-0"
            data-testid="console-title"
          >
            {consoleTitle}
          </div>
          <div className="flex items-center gap-2 md:gap-3 ml-auto">
            <NotificationBellButton onOpen={() => setNotifOpen(true)} />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium truncate max-w-[12rem]">
                {user?.name ?? user?.email}
              </div>
              <div className="text-xs text-gold" data-testid="header-role-label">
                {roleLabel}
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold shrink-0">
              {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 py-4 md:px-8 md:py-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        <NotificationSlideOver open={notifOpen} onClose={() => setNotifOpen(false)} />
      </div>
    </div>
  );
}
